/**
 * Subdomain / tenant-host helpers.
 *
 * Palbon is moving to a wildcard-subdomain model where each company gets
 * `<workspace>.palbon.app`. The root domain serves the public landing page;
 * a tenant subdomain goes straight to that workspace's login.
 *
 * This module is intentionally pure and dependency-free so the real
 * multi-tenancy work can reuse it unchanged.
 */

/** Hosts that are never a tenant, even though they parse as a subdomain. */
const RESERVED_LABELS = [
  'www',
  'app',
  'api',
  'admin',
  'mail',
  'static',
  'cdn',
  'assets',
];

const LOCAL_HOSTS = ['localhost', '127.0.0.1', '0.0.0.0', '::1'];

const IPV4_PATTERN = /^\d{1,3}(\.\d{1,3}){3}$/;

/**
 * Dev override, so the tenant redirect can be exercised on localhost without
 * editing the hosts file. Checked in this order:
 *   1. `?forceSubdomain=acme` in the URL
 *   2. `localStorage['dev-subdomain']`
 *
 * Remember to clear the localStorage key afterwards — while it is set, the
 * landing page is unreachable locally.
 */
const readDevOverride = (): string | null => {
  if (typeof window === 'undefined') return null;

  try {
    const fromQuery = new URLSearchParams(window.location.search).get('forceSubdomain');
    if (fromQuery) return fromQuery;

    return window.localStorage.getItem('dev-subdomain');
  } catch {
    // Private-mode Safari throws on localStorage access.
    return null;
  }
};

/**
 * The current tenant's workspace slug, or `null` when we are on the root
 * domain (and should therefore show the landing page).
 */
export const getSubdomain = (): string | null => {
  const override = readDevOverride();
  if (override) return override.toLowerCase();

  if (typeof window === 'undefined') return null;

  const host = window.location.hostname.toLowerCase();

  if (LOCAL_HOSTS.indexOf(host) !== -1) return null;
  if (IPV4_PATTERN.test(host)) return null;

  const labels = host.split('.');

  // Fewer than three labels means an apex domain (palbon.app), not a tenant.
  if (labels.length < 3) return null;

  const candidate = labels[0];
  if (!candidate || RESERVED_LABELS.indexOf(candidate) !== -1) return null;

  return candidate;
};

/** True when the visitor is on a tenant workspace host. */
export const isTenantSubdomain = (): boolean => getSubdomain() !== null;

/** True when the visitor is on the public root domain. */
export const isRootDomain = (): boolean => !isTenantSubdomain();

/**
 * Normalise free-typed company names into a legal subdomain label:
 * lowercase, `[a-z0-9-]` only, no leading/trailing or doubled hyphens,
 * capped at 30 characters.
 */
export const sanitizeSubdomain = (input: string): string =>
  input
    .toLowerCase()
    .replace(/[^a-z0-9-]/g, '-')
    .replace(/-{2,}/g, '-')
    .replace(/^-+/, '')
    .slice(0, 30)
    .replace(/-+$/, '');

/** The public root domain, used for display in the signup CTA. */
export const ROOT_DOMAIN = 'palbon.app';

/** Minimum workspace slug length — short labels collide too easily. */
export const MIN_SUBDOMAIN_LENGTH = 3;

/**
 * Words a tenant may not take. Checked client-side at signup so an obviously
 * unusable name is rejected immediately rather than after a round trip.
 *
 * Note this is a *format and reservation* check only — it cannot tell you
 * whether another company already holds the name. That needs the tenant table,
 * which does not exist yet, so the UI must not claim availability outright.
 */
export const isReservedSubdomain = (slug: string): boolean =>
  RESERVED_LABELS.indexOf(slug.toLowerCase()) !== -1;
